import { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Card, Alert, Spinner, Table, Modal } from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import { FaEdit, FaTrash, FaArrowLeft } from "react-icons/fa";
import axios from "axios";
import AddEditLocationsModal from "../components/modals/AddEditLocationsModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import "../styles/Location.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const Locations = () => {
  const { plantId } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const location = useLocation();

  const [locations, setLocations] = useState([]);
  const [plantData, setPlantData] = useState(location.state?.plantData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Determine if it is a warehouse location or plant locations
  const isWarehouse = location.pathname.includes("warehouse");
  const [warehouseSiteId, setWarehouseSiteId] = useState(null);
  const entityId = isWarehouse ? warehouseSiteId : plantId;

  // Fetch warehouse site_id first if needed
  const fetchWarehouseSiteId = useCallback(async () => {
    if (!isWarehouse || warehouseSiteId) return;

    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/sites/get-all-sites-based-on-type`,
        {
          params: {
            company_code: userData.company_code,
            type: "warehouse",
          },
        }
      );

      if (
        response.data.success &&
        response.data.items &&
        response.data.items.length > 0
      ) {
        const warehouse = response.data.items[0]; // Get the main warehouse
        setWarehouseSiteId(warehouse.site_id);
        setPlantData(warehouse);
      } else {
        setError("No warehouse found");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching warehouse data");
    }
  }, [isWarehouse, warehouseSiteId, userData?.company_code]);

  // Fetch plant/warehouse data only if not passed via navigation
  const fetchPlantData = useCallback(async () => {
    if (plantData || isWarehouse) {
      return;
    }

    if (!plantId || !userData?.company_code) return;

    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/sites/get-site-by-id`,
        {
          params: {
            company_code: userData.company_code,
            site_id: plantId,
            type: "plant",
          },
        }
      );

      if (response.data.success) {
        setPlantData(response.data.site);
      } else {
        setError(response.data.message || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching data");
    }
  }, [plantId, userData?.company_code, plantData, isWarehouse]);

  // Fetch locations for this plant or warehouse
  const fetchLocations = useCallback(async () => {
    if (!entityId || !userData?.company_code) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/locations/get-plant-locations`,
        {
          params: {
            company_code: userData.company_code,
            site_id: entityId,
          },
        }
      );

      if (response.data.success) {
        const locationsData =
          response.data.locations || response.data.items || [];
        setLocations(locationsData);
      } else {
        setError(response.data.message || "Failed to fetch locations");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching locations");
    } finally {
      setLoading(false);
    }
  }, [entityId, userData?.company_code]);

  useEffect(() => {
    if (isWarehouse) {
      fetchWarehouseSiteId();
    } else {
      fetchPlantData();
    }
  }, [isWarehouse, fetchWarehouseSiteId, fetchPlantData]);

  useEffect(() => {
    if (entityId) {
      fetchLocations();
    }
  }, [entityId, fetchLocations]);

  const handleEdit = (location) => {
    setEditingData(location);
    setShowAddEditModal(true);
  };

  const handleCloseModal = () => {
    setShowAddEditModal(false);
    setEditingData(null);
  };

  const handleSuccess = () => {
    setShowAddEditModal(false);
    setEditingData(null);
    fetchLocations();
  };

  const handleDeleteClick = (locationId) => {
    setItemToDelete(locationId);
    setShowConfirmDelete(true);
  };

  const confirmDeleteLocation = async () => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/api/admin/locations/delete-location/${itemToDelete}`,
        {
          params: {
            company_code: userData.company_code,
          },
        }
      );

      if (response.data.success) {
        fetchLocations();
      } else {
        setError(response.data.message || "Failed to delete location");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error deleting location");
    }
    setShowConfirmDelete(false);
    setItemToDelete(null);
  };

  const handleBackToPlants = () => {
    if (isWarehouse) {
      navigate("/admin-dashboard"); // Go back to dashboard for warehouse
    } else {
      navigate("/manage-plants");
    }
  };

  return (
    <div className="admin-container mt-4">
      <div className="d-flex justify-content-between align-items-center style-header">
        <h1>Location Management</h1>

        {!isWarehouse && (
          <Button
            variant="secondary"
            onClick={handleBackToPlants}
            className="d-flex align-items-center gap-2"
          >
            <FaArrowLeft /> Back to Plants
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="loc-titles">
              Locations List for {plantData?.site_name || "Warehouse"} (
              {plantData?.site_code || "N/A"})
            </h3>
            <Button onClick={() => setShowAddEditModal(true)}>
              Add Location
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : locations.length === 0 ? (
            <Alert variant="info">No locations found for this plant</Alert>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>Location Name</th>
                  <th>Location Code</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr key={location.location_id}>
                    <td>{location.location_name}</td>
                    <td>{location.location_code || "N/A"}</td>
                    <td>
                      {location.description ||
                        "No Additional Information Provided"}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <FaEdit
                          className="action-icon mt-1"
                          onClick={() => {
                            handleEdit(location);
                          }}
                          title="Edit"
                          style={{ cursor: "pointer", color: "#007bff" }}
                        />
                        <FaTrash
                          className="action-icon text-danger mt-1"
                          onClick={() => {
                            handleDeleteClick(location.location_id);
                          }}
                          title="Delete"
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Location Modal */}
      {showAddEditModal && (
        <Modal
          show={showAddEditModal}
          onHide={handleCloseModal}
          dialogClassName="vcm-modal-dialog"
          centered
        >
          <Modal.Header className="vcm-modal-header" closeButton>
            <Modal.Title>
              {editingData ? "Update Location" : "Add Location"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AddEditLocationsModal
              editData={editingData}
              onSuccess={handleSuccess}
              plantId={entityId}
              plantData={plantData}
              isWarehouse={isWarehouse}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showConfirmDelete}
        onHide={() => {
          setShowConfirmDelete(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDeleteLocation}
        title="Delete Location"
        message="Are you sure you want to delete this location? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={loading}
      />
    </div>
  );
};

export default Locations;
